import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { TrackingModule } from '@freelancer/tracking';
import { Subscription } from 'rxjs';
import { ButtonComponent } from '../button/button.component';
import { generateUniqueID } from '../helpers/helpers';
import {
  flChevronLeft,
  flChevronRight,
  IconColor,
  IconModule,
  IconSize,
  provideFlIcons,
} from '../icon';
import type { SelectItem } from '../select';
import { SelectSize } from '../select';
import { SegmentedDateInputYearOrderSource } from './segmented-date-input-year-order.service';
import { SelectComponent } from '../select/select.component';

/**
 * Replaces Angular Material's calendar header with month and year dropdowns.
 *
 * Material's stock header reaches a year by paging a 24-year grid, which is what
 * made picking a birth year slow — and, with no `min` bound, what let the range
 * run past year 0 into fake BC years (T333685). A dropdown removes paging from
 * the year axis entirely: the arrows here only ever step a month.
 *
 * Material builds this through a `ComponentPortal` and hands it `MatCalendar`
 * via DI, so `calendar.activeDate` is the whole navigation API — setting it is
 * exactly what the stock header does.
 */
@Component({
  standalone: true,
  selector: 'fl-segmented-date-input-calendar-header',
  imports: [
    ButtonComponent,
    IconModule,
    ReactiveFormsModule,
    SelectComponent,
    TrackingModule,
  ],
  providers: [provideFlIcons({ flChevronLeft, flChevronRight })],
  template: `
    <div
      class="CalendarHeader"
      flTrackingSection="SegmentedDateInputCalendar"
    >
      <fl-select
        class="CalendarHeader-month"
        flTrackingLabel="CalendarMonth"
        [control]="monthControl"
        [id]="uniqueId + '-calendarMonth'"
        [label]="monthLabel"
        [options]="monthOptions"
        [size]="SelectSize.SMALL"
      ></fl-select>
      <fl-select
        class="CalendarHeader-year"
        flTrackingLabel="CalendarYear"
        [control]="yearControl"
        [id]="uniqueId + '-calendarYear'"
        [label]="yearLabel"
        [options]="yearOptions"
        [size]="SelectSize.SMALL"
      ></fl-select>
      <div class="CalendarHeader-arrows">
        <fl-button
          class="CalendarHeader-arrow"
          flTrackingLabel="PreviousMonth"
          [disabled]="!previousEnabled"
          [label]="previousLabel"
          (click)="handlePreviousMonth()"
        >
          <fl-icon
            [color]="IconColor.INHERIT"
            [name]="'flChevronLeft'"
            [size]="IconSize.SMALL"
          ></fl-icon>
        </fl-button>
        <fl-button
          class="CalendarHeader-arrow"
          flTrackingLabel="NextMonth"
          [disabled]="!nextEnabled"
          [label]="nextLabel"
          (click)="handleNextMonth()"
        >
          <fl-icon
            [color]="IconColor.INHERIT"
            [name]="'flChevronRight'"
            [size]="IconSize.SMALL"
          ></fl-icon>
        </fl-button>
      </div>
    </div>
  `,
  styleUrls: ['./segmented-date-input-calendar-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegmentedDateInputCalendarHeaderComponent
  implements OnInit, OnDestroy
{
  private calendar = inject<MatCalendar<Date>>(MatCalendar);
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private cd = inject(ChangeDetectorRef);
  /**
   * Lets the header list years the same way round as the field's own year
   * dropdown. Optional so the header still works if it is ever used under a
   * plain `mat-datepicker`.
   */
  private yearOrderSource = inject(SegmentedDateInputYearOrderSource, {
    optional: true,
  });

  IconColor = IconColor;
  IconSize = IconSize;
  SelectSize = SelectSize;

  readonly uniqueId = generateUniqueID();
  readonly monthControl = new FormControl<number | null>(null, {
    nonNullable: true,
  });
  readonly yearControl = new FormControl<number | null>(null, {
    nonNullable: true,
  });

  monthOptions: readonly SelectItem<number | null>[] = [];
  yearOptions: readonly SelectItem<number | null>[] = [];

  readonly monthLabel = $localize`Month`;
  readonly yearLabel = $localize`Year`;
  readonly previousLabel = $localize`Previous month`;
  readonly nextLabel = $localize`Next month`;

  /** Guards against syncing the dropdowns back out as a navigation. */
  private isSyncingFromCalendar = false;
  private readonly subscriptions = new Subscription();

  get previousEnabled(): boolean {
    const { minDate } = this.calendar;
    return !minDate || !this.isSameMonth(this.calendar.activeDate, minDate);
  }

  get nextEnabled(): boolean {
    const { maxDate } = this.calendar;
    return !maxDate || !this.isSameMonth(this.calendar.activeDate, maxDate);
  }

  ngOnInit(): void {
    // Month names come from the adapter rather than LocalizedDateFns: the
    // adapter is guaranteed injectable here (Material's own header takes it),
    // whereas LocalizedDateFns is provided by UiModule and an
    // overlay-instantiated component can't be relied on to reach it.
    this.monthOptions = this.dateAdapter
      .getMonthNames('long')
      .map((month, index) => ({ value: index, displayText: month }));
    this.yearOptions = this.buildYearOptions();

    this.syncFromCalendar();

    // Fires whenever the calendar values move, including from the day grid's own
    // keyboard navigation, so the dropdowns never drift from the day grid.
    this.subscriptions.add(
      this.calendar.stateChanges.subscribe(() => {
        this.syncFromCalendar();
        this.cd.markForCheck();
      }),
    );

    this.subscriptions.add(
      this.monthControl.valueChanges.subscribe(() => this.navigate()),
    );
    this.subscriptions.add(
      this.yearControl.valueChanges.subscribe(() => this.navigate()),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  handlePreviousMonth(): void {
    this.calendar.activeDate = this.dateAdapter.addCalendarMonths(
      this.calendar.activeDate,
      -1,
    );
  }

  handleNextMonth(): void {
    this.calendar.activeDate = this.dateAdapter.addCalendarMonths(
      this.calendar.activeDate,
      1,
    );
  }

  /**
   * Moves the calendar to the selected month and year. The day of the month is
   * clamped so jumping from a 31-day month to February doesn't overflow into
   * March.
   */
  private navigate(): void {
    if (this.isSyncingFromCalendar) {
      return;
    }

    const month = this.monthControl.value;
    const year = this.yearControl.value;
    if (month === null || year === null) {
      return;
    }

    const firstOfMonth = this.dateAdapter.createDate(year, month, 1);
    const day = Math.min(
      this.dateAdapter.getDate(this.calendar.activeDate),
      this.dateAdapter.getNumDaysInMonth(firstOfMonth),
    );
    this.calendar.activeDate = this.dateAdapter.createDate(year, month, day);
  }

  private syncFromCalendar(): void {
    const { activeDate } = this.calendar;
    this.isSyncingFromCalendar = true;
    this.monthControl.setValue(this.dateAdapter.getMonth(activeDate), {
      emitEvent: false,
    });
    this.yearControl.setValue(this.dateAdapter.getYear(activeDate), {
      emitEvent: false,
    });
    this.isSyncingFromCalendar = false;
  }

  private isSameMonth(first: Date, second: Date): boolean {
    return (
      this.dateAdapter.getYear(first) === this.dateAdapter.getYear(second) &&
      this.dateAdapter.getMonth(first) === this.dateAdapter.getMonth(second)
    );
  }

  /**
   * The year list comes straight from the calendar's bounds.
   * `fl-segmented-date-input` always binds them (falling back to its own window
   * when the caller gives none), which is why an unbounded calendar collapsing to
   * the active year alone is acceptable here — this header is not exported for
   * use under a bare `mat-datepicker`.
   */
  private buildYearOptions(): readonly SelectItem<number | null>[] {
    const activeYear = this.dateAdapter.getYear(this.calendar.activeDate);
    const firstYear = this.calendar.minDate
      ? this.dateAdapter.getYear(this.calendar.minDate)
      : activeYear;
    const lastYear = this.calendar.maxDate
      ? this.dateAdapter.getYear(this.calendar.maxDate)
      : activeYear;

    const years: number[] = [];
    for (let year = firstYear; year <= lastYear; year++) {
      years.push(year);
    }
    if (this.yearOrderSource?.yearOrder === 'descending') {
      years.reverse();
    }

    return years.map(year => ({ value: year, displayText: String(year) }));
  }
}
